import { z } from 'zod';
import type OpenAI from 'openai';
import type {
	ChatCompletionCreateParamsStreaming,
	ChatCompletionMessageParam,
} from 'openai/resources/chat/completions';

export const toolSchema = z.object({
	modelId: z
		.string()
		.nullish()
		.describe(
			"The existing model id to update if it's an update. Determine the modelId from the conversation history if the request is about making changes to an existing model.",
		),
	prompt: z.string().describe(
		`Detailed instructions for the data model to be generated or instructions on what to update if an existing model. 
Use a dimensional model based on Ralph Kimball's methodology, with inline date handling.
Do not create separate Date or Time dimension tables; instead, include a date column directly in the fact table.

If no business domain is specified, choose one from the list of domains in Ralph Kimball's book.

Use PascalCase for all table names and column names.

First, generate independent dimension tables using singular nouns (e.g., Customer, Product, Book, Event).

Second, generate a fact table (e.g., Transaction or SalesFact) with transaction-level granularity referencing relevant dimension tables. The fact table MUST include:
- A primary key (e.g., TransactionID or FactID) to uniquely identify each row.
- Foreign key columns (e.g., CustomerID, ProductID, BookID, EventID) that reference the primary keys of relevant associated dimension tables (e.g., Customer, Product, Book, Event), following a star schema structure.
- A date column representing the date of the transaction.
- At least three quantitative fact columns (e.g., measures such as amount, quantity, or count).

Finally, remove dimension tables that are not referenced by the fact table.
`,
	),
});

const zReference = z
	.object({
		table: z
			.string()
			.describe(
				'The name of the table being referenced (e.g., "Customer")',
			),
		column: z
			.string()
			.describe(
				'The primary key column in the referenced table (e.g., "CustomerID")',
			),
	})
	.describe('Details for a foreign key reference to another table.');

const zColumn = () =>
	z
		.object({
			id: z.string().optional(),
			columnName: z.string(),
			isPrimaryKey: z
				.boolean()
				.describe('If this column is a primary key'),
			isForeignKey: z
				.boolean()
				.describe('If this column is a foreign key'),
			type: z.enum(['numeric', 'text', 'datetime']),
			fakerType: z.string().describe('faker.js type'),
			fakerOptions: z
				.optional(z.string())
				.describe('faker.js options, stringified'),
			referenceTo: z
				.optional(zReference)
				.describe(
					'If this column is a foreign key, it must reference a primary key column in a dimension table.',
				),
		})
		.describe('column of the table');

const zTable = () =>
	z
		.object({
			id: z.string().optional(),
			tableName: z.string().describe('name of the table'),
			tableType: z
				.enum(['fact', 'dimension'])
				.describe('type of the table'),
			rowCount: z
				.number()
				.int()
				.nonnegative()
				.describe('number of rows in the table.'),
			columns: z.array(zColumn()).describe('columns of the table'),
		})
		.describe('table of the model');

/** Persisted logical data model document (tool results / canvas). */
export const dataModelDocumentSchema = z.object({
	dbName: z.string(),
	tables: z.array(zTable()),
	summary: z.string().describe('summary of the model'),
	error: z
		.string()
		.optional()
		.describe('error message if the model generation failed'),
});

export type DataModelDocument = z.infer<typeof dataModelDocumentSchema>;
export type DataModelTable = DataModelDocument['tables'][number];
export type DataModelColumn = DataModelTable['columns'][number];

export const dataModelJsonPatchSchema = z.union([
	z.object({
		op: z.enum(['add', 'replace']),
		path: z.string(),
		value: z.union([
			zColumn(),
			zTable(),
			z.string(),
			z.number(),
			z.boolean(),
		]),
	}),
	z.object({
		op: z.enum(['remove']),
		path: z.string(),
	}),
]);

export type DataModelJsonPatch = z.infer<typeof dataModelJsonPatchSchema>;

export interface BuildDataModelEngineResult {
	dataSourceTitle: string;
	summary: string;
	modelId: string;
	usageInstructions: string;
}

export type DataModelStreamHistoryPayload =
	| {
			kind: 'request';
			opts: ChatCompletionCreateParamsStreaming;
	  }
	| {
			kind: 'complete';
			opts: ChatCompletionCreateParamsStreaming;
			messages: ChatCompletionMessageParam[];
			/** Snapshot stored with tool history (last incremental document before final patch pass). */
			result: DataModelDocument;
	  };

export interface BuildDataModelContext {
	toolCallId: string;
	/** Starting document; tool loads from session when updating an existing model. */
	initialDocument: DataModelDocument;
	openAIClient?: OpenAI;
	activeSessionId?: string;
	onDocumentUpdate?: (document: DataModelDocument) => void;
	onStreamHistory?: (payload: DataModelStreamHistoryPayload) => void;
}
